import React, { useState, useEffect, useRef } from 'react';

const DESKTOP_STEPS = [
  {
    selector: '[data-tour="stats"]',
    title: '📊 Stats at a Glance',
    desc: 'Your live platform stats — Total Kahanis, Offer Rate, Ghost Rate, Rejected, and In Progress. Click any stat to instantly filter the feed below.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="nav"]',
    title: '🗂 Navigation',
    desc: 'Jump between Home, Jobs, Top Questions, Company Heat Map, Most Liked, and Salary Guide from here.',
    position: 'right',
  },
  {
    selector: '[data-tour="share"]',
    title: '✍️ Share Your Kahani',
    desc: 'Click "My Kahani" to access your personal space and share your interview experience. It takes less than 2 minutes and helps thousands of job seekers.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="feed"]',
    title: '📰 The Community Feed',
    desc: 'Real interview stories from the community — upvote, comment, react, and save the ones that resonate with you. Click any card to read the full Kahani.',
    position: 'top',
  },
  {
    selector: '[data-tour="live"]',
    title: '⚡ Live Activity',
    desc: 'Watch the community in real time — new kahanis, upvotes, reactions, and comments as they happen. Click any activity to jump straight to that Kahani.',
    position: 'left',
  },
];

const MOBILE_STEPS = [
  {
    selector: '[data-tour="stats"]',
    title: '📊 Stats at a Glance',
    desc: 'Your live platform stats — Total Kahanis, Offer Rate, Ghost Rate, and more. Tap any stat to filter the feed instantly.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="nav-mobile"]',
    title: '🗂 Navigation Menu',
    desc: 'Tap the menu icon to explore Most Liked, Company Heat Map, Jobs, Salary Guide, Top Questions, and more.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="share-mobile"]',
    title: '✍️ Share Your Kahani',
    desc: 'Tap "My Kahani" to access your personal space and share your interview experience. It takes less than 2 minutes!',
    position: 'top',
  },
  {
    selector: '[data-tour="feed"]',
    title: '📰 The Community Feed',
    desc: 'Real interview stories from the community — upvote, comment, react, and save the ones that resonate with you.',
    position: 'top',
  },
  {
    selector: '[data-tour="live-mobile"]',
    title: '⚡ Live Activity',
    desc: 'Tap the Live button to watch the community in real time — new kahanis, upvotes, reactions as they happen.',
    position: 'top',
  },
];

function getRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
}

const PAD = 10; // spotlight padding

export default function DashboardTour({ onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [visible, setVisible] = useState(false);
  const rafRef = useRef();

  const STEPS = window.innerWidth < 768 ? MOBILE_STEPS : DESKTOP_STEPS;
  const step = STEPS[idx];

  useEffect(() => {
    const update = () => {
      const r = getRect(step.selector);
      setRect(r);
      if (r) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const tw = 310, th = 180; // tooltip approx size
        let style = { position: 'fixed', width: tw, zIndex: 9999 };

        if (step.position === 'bottom') {
          style.top = r.bottom + PAD + 12;
          style.left = Math.min(Math.max(r.left + r.width / 2 - tw / 2, 12), vw - tw - 12);
        } else if (step.position === 'top') {
          style.top = Math.max(r.top - th - PAD - 12, 12);
          style.left = Math.min(Math.max(r.left + r.width / 2 - tw / 2, 12), vw - tw - 12);
        } else if (step.position === 'right') {
          style.top = Math.min(Math.max(r.top + r.height / 2 - th / 2, 12), vh - th - 12);
          style.left = Math.min(r.right + PAD + 12, vw - tw - 12);
        } else if (step.position === 'left') {
          style.top = Math.min(Math.max(r.top + r.height / 2 - th / 2, 12), vh - th - 12);
          style.left = Math.max(r.left - tw - PAD - 12, 12);
        }
        setTooltipStyle(style);
      }
    };

    update();
    // Re-measure on scroll/resize
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // Small delay for initial paint
    const t = setTimeout(() => setVisible(true), 50);
    const rafId = rafRef.current;
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      clearTimeout(t);
      cancelAnimationFrame(rafId);
    };
  }, [idx, step]);

  // Scroll element into view
  useEffect(() => {
    const el = document.querySelector(step.selector);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [idx, step.selector]);

  const next = () => {
    setVisible(false);
    setTimeout(() => { setIdx(i => i + 1); setVisible(false); }, 200);
  };

  const done = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const isLast = idx === STEPS.length - 1;

  // Spotlight clip — dark overlay with a cut-out rect
  const spotlightStyle = rect ? {
    clipPath: `polygon(
      0% 0%, 100% 0%, 100% 100%, 0% 100%,
      0% ${rect.top - PAD}px,
      ${rect.left - PAD}px ${rect.top - PAD}px,
      ${rect.left - PAD}px ${rect.bottom + PAD}px,
      ${rect.right + PAD}px ${rect.bottom + PAD}px,
      ${rect.right + PAD}px ${rect.top - PAD}px,
      0% ${rect.top - PAD}px
    )`,
  } : {};

  return (
    <>
      {/* Dark overlay with spotlight cutout */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9990,
        background: 'rgba(0,0,0,0.62)',
        transition: 'clip-path 0.4s cubic-bezier(0.16,1,0.3,1)',
        pointerEvents: 'none',
        ...spotlightStyle,
      }} />

      {/* Spotlight border glow */}
      {rect && (
        <div style={{
          position: 'fixed', zIndex: 9991, pointerEvents: 'none',
          top: rect.top - PAD - 2,
          left: rect.left - PAD - 2,
          width: rect.width + PAD * 2 + 4,
          height: rect.height + PAD * 2 + 4,
          borderRadius: 14,
          border: '2px solid rgba(14,165,233,0.7)',
          boxShadow: '0 0 0 4px rgba(14,165,233,0.15), 0 0 28px rgba(14,165,233,0.25)',
          transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
        }} />
      )}

      {/* Tooltip card */}
      <div style={{
        ...tooltipStyle,
        background: '#ffffff',
        borderRadius: 18,
        padding: '22px 22px 18px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.06)',
        zIndex: 9999,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.97)',
        transition: 'opacity 0.25s ease, transform 0.25s ease',
      }}>
        {/* Step counter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                width: i === idx ? 16 : 6, height: 6, borderRadius: 3,
                background: i <= idx ? '#0ea5e9' : '#e2e8f0',
                transition: 'all 0.3s ease',
              }} />
            ))}
          </div>
          <button
            onClick={done}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 2 }}
          >×</button>
        </div>

        <div style={{ fontWeight: 900, fontSize: 15, color: '#0f172a', marginBottom: 8, fontFamily: 'var(--display)' }}>
          {step.title}
        </div>
        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 18 }}>
          {step.desc}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={done}
            style={{
              flex: 1, padding: '9px', borderRadius: 10, border: '1.5px solid #e2e8f0',
              background: 'transparent', color: '#64748b', fontSize: 13, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'var(--sans)', transition: 'all 0.15s',
            }}
          >
            Skip Tour
          </button>
          <button
            onClick={isLast ? done : next}
            style={{
              flex: 2, padding: '9px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
              color: '#fff', fontSize: 13, fontWeight: 800,
              cursor: 'pointer', fontFamily: 'var(--sans)',
              boxShadow: '0 4px 12px rgba(14,165,233,0.3)', transition: 'all 0.15s',
            }}
          >
            {isLast ? '🎉 Done!' : `Next →  ${idx + 1}/${STEPS.length}`}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(14,165,233,0.15), 0 0 28px rgba(14,165,233,0.25); }
          50%       { box-shadow: 0 0 0 8px rgba(14,165,233,0.08), 0 0 40px rgba(14,165,233,0.35); }
        }
      `}</style>
    </>
  );
}
